import { Database, Q } from '@nozbe/watermelondb';

import Entry from '../../Entry';
import EventContact from '../../EventContact';
import EventContactReminder from '../../EventContactReminder';
import EventHorse from '../../EventHorse';
import Horse from '../../Horse';
import EventUser from '../../EventUser';
import {
    ContactDBServiceOptions,
    DBServiceOptionsWithImagesAndValidation,
    EntryDBServiceOptions,
    HorseDBServiceOptions,
} from '../../../../types/dbService';
import { EntryModel } from '../../../../types/Entries';
import {
    EVENT_CONTACT_STATUS,
    EventContactModel,
} from '../../../../types/EventContact';
import { EventContactReminderModel } from '../../../../types/EventContactReminder';
import { EventHorseModel } from '../../../../types/EventHorse';
import {
    EventModel,
    EventPayload,
    EventCategory,
    PreparedDataOnAdd,
    ContactInvitationsPayload,
} from '../../../../types/Events';
import { HorseModel } from '../../../../types/Horses';
import { InventoryChangeModel } from '../../../../types/InventoryChange';
import { LocationModel } from '../../../../types/Location';
import { EventUserModel } from '../../../../types/EventUser';
import { Identificator } from '../../../../types/Identificator';
import { diffBetweenDatesByRange, isFuture } from '../../../../utils/date';
import { getTimeZone } from '../../../../utils/timezone';
import {
    DBServiceLogger,
    DBWriterLoggerFunction,
} from '../../../../types/logger';
import { mapRecords, prepareEventLocation } from './common';
import { getLocationIdToAssign } from '../../../utils';
import Location from '../../Location';
import moment from 'moment';
import { UserModel } from '../../../../types/User';
import Contact from '../../Contact';
import { HorseContactModel } from '../../../../types/HorseContact';
import { DatabaseValidation } from '../../../../types/validation';

const prepareUpdateLocation = prepareEventLocation;

const prepareUpdateEvent = async ({
    eventElement,
    payload,
    notifyContacts,
    locationElement,
    serviceOptions,
    logger,
}: {
    eventElement: EventModel;
    payload: EventPayload;
    notifyContacts?: boolean;
    locationElement: LocationModel | null | undefined;
    serviceOptions: DBServiceOptionsWithImagesAndValidation;
    logger: DBServiceLogger;
}) => {
    let fallbackLocationExists = false;

    const locationService = new Location({
        database: eventElement.database,
        imageService: serviceOptions.imageService,
        logDBAction: logger.logDBAction,
        validation: serviceOptions.validation,
    });

    if (eventElement.locationId) {
        const eventLocation = await locationService.getByParam(
            'id',
            eventElement.locationId,
        );
        fallbackLocationExists = eventLocation.length > 0;
    }

    const now = moment().toISOString();

    const updatedEvent = await eventElement.prepareUpdate((event) => {
        event.title = payload.title;
        event.address = payload.address || null;
        event.startsTime = payload.startsTime;
        event.endsTime = payload.endsTime;
        event.notes = payload.notes || null;
        event.userReminder = payload.userReminder;
        event.contactsReminder = payload.contactsReminder;
        event.ownersReminder = payload.ownersReminder;
        event.sendInvitesToContactsAt = notifyContacts
            ? now
            : event.sendInvitesToContactsAt;
        event.locationId = getLocationIdToAssign({
            location: payload.location,
            locationElementId: locationElement?.id,
            address: payload.address,
            fallbackLocationId: event.locationId,
            logError: logger.error,
            logWarn: logger.warn,
            entityName: 'event',
            fallbackLocationExists,
        });
        event.userId = eventElement.userId;
        event.organisationId = eventElement.organisationId;
        event.timezone =
            payload.timezone || eventElement.timezone || getTimeZone();
        event.category = payload.category || EventCategory.standard;
        event.isRecurring = payload.isRecurring ?? eventElement.isRecurring;
        event.recurrenceEndDate =
            payload.recurrenceEndDate ?? eventElement.recurrenceEndDate;
        event.recurrencePattern =
            payload.recurrencePattern ?? eventElement.recurrencePattern;
        event.originalEventId =
            payload.originalEventId ?? eventElement.originalEventId;
    });

    return updatedEvent;
};

const updateHorsesRelatedData = async ({
    eventElement,
    horses,
    eventId,
    payload,
    serviceOptions,
    database,
    validation,
}: {
    eventElement: EventModel;
    horses: Identificator[] | undefined;
    eventId: string;
    payload: EventPayload;
    serviceOptions: HorseDBServiceOptions;
    database: Database;
    validation: DatabaseValidation;
}) => {
    let eventHorsesToAdd: EventHorseModel[] = [];
    let eventHorsesToDelete: EventHorseModel[] = [];
    let horsesToUpdate: HorseModel[] = [];
    let relatedEntriesToUpdate: EntryModel[] = [];

    const eventHorseService = new EventHorse({
        database,
        imageService: serviceOptions.imageService,
        logDBAction: serviceOptions.logger.logDBAction,
    });
    const entryServiceOptions: EntryDBServiceOptions = {
        database,
        imageService: serviceOptions.imageService,
        logger: serviceOptions.logger,
        validation,
    };
    const horseService = new Horse(serviceOptions);
    const entryService = new Entry(entryServiceOptions);

    const initialHorses = await eventElement.horses.fetch();

    const eventHorsesCollection =
        database.collections.get<EventHorseModel>('event_horses');

    const eventHorses = await eventHorseService.getByParam(
        'event_id',
        eventElement.id,
    );

    const eventStartsTime = payload.startsTime;

    if (horses) {
        for (let horse of horses) {
            if (
                !initialHorses.find(
                    (initialHorse) => initialHorse.id === horse.id,
                )
            ) {
                const eventHorseToAdd =
                    await eventHorsesCollection.prepareCreate((eventHorse) => {
                        eventHorse.eventId = eventElement.id;
                        eventHorse.horseId = horse.id;
                        eventHorse.userId = eventElement.userId;
                        eventHorse.organisationId = eventElement.organisationId;
                    });

                eventHorsesToAdd.push(eventHorseToAdd);
            }
        }
    }

    if (horses) {
        await Promise.all(
            eventHorses.map(async (eventHorse) => {
                if (!horses.find((horse) => horse.id === eventHorse.horseId)) {
                    eventHorsesToDelete.push(eventHorse.prepareMarkAsDeleted());

                    const horseEntry = await entryService.getByParams({
                        event_id: eventId,
                        horse_id: eventHorse.horseId,
                    });

                    if (horseEntry[0]) {
                        const entryToUpdate = await horseEntry[0].prepareUpdate(
                            (relatedEntry) => {
                                relatedEntry.eventId = '';
                                relatedEntry.loggedTime = eventStartsTime;
                            },
                        );

                        relatedEntriesToUpdate.push(entryToUpdate);
                    }
                }
            }),
        );
    }

    if (horses?.length) {
        for (const horse of horses) {
            const updates: {
                shoeingCycleBadge?: boolean;
                name?: string;
                shoeingCycle?: number;
            } = {};

            const isNewHorse = !initialHorses.find(
                (initialHorse) => initialHorse.id === horse.id,
            );

            if (isNewHorse && isFuture(eventStartsTime)) {
                updates.shoeingCycleBadge = false;
            }

            const needsShoeingCycleLookup = payload.updateHorseShoeingCycle;

            if (!needsShoeingCycleLookup && Object.keys(updates).length === 0) {
                continue;
            }

            const horseEntity = await horseService.getByID(horse.id);

            if (needsShoeingCycleLookup) {
                const horseEvents = await horseEntity.events.fetch();
                const pastEvent = horseEvents.find(
                    (event) => !isFuture(event.startsTime),
                );

                if (horseEntity.shoeingCycle === 0 && pastEvent) {
                    const weekDiff = diffBetweenDatesByRange(
                        payload.startsTime,
                        pastEvent.startsTime,
                        'weeks',
                    );

                    if (weekDiff >= 3 && weekDiff <= 12) {
                        updates.name = horseEntity.name;
                        updates.shoeingCycle = weekDiff;
                    }
                }
            }

            if (Object.keys(updates).length === 0) {
                continue;
            }

            const horseToUpdate = await horseEntity.prepareUpdate((horse) => {
                Object.assign(horse, updates);
            });

            horsesToUpdate.push(horseToUpdate);
        }
    }

    return {
        eventHorsesToAdd,
        eventHorsesToDelete,
        horsesToUpdate,
        relatedEntriesToUpdate,
    };
};

const updateContactsRelatedData = async ({
    eventElement,
    eventId,
    contacts,
    contactsReminder,
    contactsReminderTimeBefore,
    ownersReminder,
    userReminder,
    invitations,
    notifyContacts,
    addNewHorsesToContact,
    newHorses,
    database,
    serviceOptions,
    contactServiceOptions,
}: {
    eventElement: EventModel;
    eventId: string;
    contacts: Identificator[] | undefined;
    contactsReminder: boolean;
    contactsReminderTimeBefore?: string;
    ownersReminder: boolean;
    userReminder?: string | null;
    invitations?: ContactInvitationsPayload;
    notifyContacts?: boolean;
    addNewHorsesToContact?: boolean;
    newHorses?: Identificator[];
    database: Database;
    serviceOptions: DBServiceOptionsWithImagesAndValidation;
    contactServiceOptions: ContactDBServiceOptions;
}) => {
    let eventContactsToAdd: EventContactModel[] = [];
    let eventContactsToUpdate: EventContactModel[] = [];
    let eventContactsToDelete: EventContactModel[] = [];
    let eventContactRemindersToAdd: EventContactReminderModel[] = [];
    let eventContactRemindersToUpdate: EventContactReminderModel[] = [];
    let eventContactRemindersToDelete: EventContactReminderModel[] = [];
    let horseContactsToAdd: HorseContactModel[] = [];
    let horseContactsToDelete: HorseContactModel[] = [];

    const initialContacts = await eventElement.contacts.fetch();
    const eventContactsCollection =
        database.collections.get<EventContactModel>('event_contacts');
    const eventContactService = new EventContact(serviceOptions);
    const eventContactRemindersCollection =
        database.collections.get<EventContactReminderModel>(
            'event_contact_reminders',
        );
    const eventContactRemindersService = new EventContactReminder(
        serviceOptions,
    );
    const eventContacts = await eventContactService.fetchByParam(
        'event_id',
        eventElement.id,
    );

    if (contacts) {
        await Promise.all(
            contacts.map(async (contact) => {
                const initialContact = initialContacts.find(
                    (ic) => ic.id === contact.id,
                );

                // userReminder as fallback for feature flag compatibility
                const contactReminderTime =
                    contactsReminderTimeBefore || userReminder;
                const shouldRemindContact = contactsReminder || ownersReminder;

                if (!initialContact) {
                    const eventContactToAdd =
                        await eventContactsCollection.prepareCreate(
                            (eventContact) => {
                                eventContact.eventId = eventElement.id;
                                eventContact.contactId = contact.id;
                                eventContact.userId = eventElement.userId;
                                eventContact.organisationId =
                                    eventElement.organisationId;
                                eventContact.sentSms =
                                    invitations?.[contact.id].sms ?? false;
                                eventContact.sentEmail =
                                    invitations?.[contact.id].email ?? false;
                                eventContact.status =
                                    EVENT_CONTACT_STATUS.AWAITING;
                            },
                        );

                    if (contactReminderTime && shouldRemindContact) {
                        const eventContactReminderToAdd =
                            await eventContactRemindersCollection.prepareCreate(
                                (eventContactReminder) => {
                                    eventContactReminder.eventId = eventId;
                                    eventContactReminder.timeBefore =
                                        contactReminderTime;
                                    eventContactReminder.eventContactId =
                                        eventContactToAdd.id;
                                    eventContactReminder.userId =
                                        eventElement.userId;
                                    eventContactReminder.organisationId =
                                        eventElement.organisationId;
                                },
                            );
                        eventContactRemindersToAdd.push(
                            eventContactReminderToAdd,
                        );
                    }

                    eventContactsToAdd.push(eventContactToAdd);
                } else {
                    const eventContact = eventContacts.find(
                        (ec) => ec.contactId === initialContact.id,
                    );

                    const eventContactReminder =
                        await eventContactRemindersService.getByParam(
                            'event_contact_id',
                            eventContact?.id,
                        );
                    if (
                        contactReminderTime &&
                        shouldRemindContact &&
                        eventContactReminder.length === 0
                    ) {
                        const eventContactReminderToAdd =
                            await eventContactRemindersCollection.prepareCreate(
                                (eventContactReminder) => {
                                    eventContactReminder.eventId = eventId;
                                    eventContactReminder.timeBefore =
                                        contactReminderTime;
                                    eventContactReminder.eventContactId =
                                        eventContact?.id;
                                    eventContactReminder.userId =
                                        eventElement.userId;
                                    eventContactReminder.organisationId =
                                        eventElement.organisationId;
                                },
                            );
                        eventContactRemindersToAdd.push(
                            eventContactReminderToAdd,
                        );
                    }
                }
            }),
        );
    }

    if (contacts) {
        await Promise.all(
            eventContacts.map(async (eventContact) => {
                const eventContactReminder =
                    await eventContactRemindersService.getByParam(
                        'event_contact_id',
                        eventContact.id,
                    );

                if (
                    !contacts.find(
                        (contact) => contact.id === eventContact.contactId,
                    )
                ) {
                    eventContactsToDelete.push(
                        eventContact.prepareMarkAsDeleted(),
                    );

                    if (eventContactReminder.length !== 0) {
                        eventContactRemindersToDelete.push(
                            eventContactReminder[0].prepareMarkAsDeleted(),
                        );
                    }
                } else {
                    const contactInvitation =
                        invitations?.[eventContact.contactId];
                    const invitationChanged =
                        contactInvitation &&
                        (eventContact.sentSms !== contactInvitation?.sms ||
                            eventContact.sentEmail !==
                                contactInvitation?.email);
                    if (invitationChanged || notifyContacts) {
                        const eventContactToUpdate = eventContact.prepareUpdate(
                            (ec) => {
                                if (notifyContacts) {
                                    ec.status = EVENT_CONTACT_STATUS.AWAITING;
                                }

                                ec.sentSms =
                                    contactInvitation?.sms ?? ec.sentSms;
                                ec.sentEmail =
                                    contactInvitation?.email ?? ec.sentEmail;
                            },
                        );

                        eventContactsToUpdate.push(eventContactToUpdate);
                    }

                    // userReminder as fallback for feature flag compatibility
                    const contactReminderTime =
                        contactsReminderTimeBefore || userReminder;
                    const shouldRemindContact =
                        contactsReminder || ownersReminder;

                    if (eventContactReminder.length !== 0) {
                        if (shouldRemindContact) {
                            if (
                                contactReminderTime &&
                                eventContactReminder[0].timeBefore !==
                                    contactReminderTime
                            ) {
                                const eventContactReminderToUpdate =
                                    await eventContactReminder[0].prepareUpdate(
                                        (reminder) => {
                                            reminder.timeBefore =
                                                contactReminderTime;
                                        },
                                    );
                                eventContactRemindersToUpdate.push(
                                    eventContactReminderToUpdate,
                                );
                            }
                        } else {
                            eventContactRemindersToDelete.push(
                                eventContactReminder[0].prepareMarkAsDeleted(),
                            );
                        }
                    }
                }
            }),
        );
    }

    const contactID = contacts?.[0]?.id;

    if (addNewHorsesToContact && contactID) {
        const horsesCollection = database.collections.get<HorseModel>('horses');
        const contactService = new Contact(contactServiceOptions);
        const contact = await contactService.getByID(contactID);
        const contactHorses = await horsesCollection
            .query(
                Q.experimentalJoinTables(['horse_contacts']),
                Q.on('horse_contacts', 'contact_id', contactID),
            )
            .fetch();
        const contactHorsesIds = [
            ...contactHorses.map((horse) => ({ id: horse.id })),
            ...(newHorses ?? []),
        ];
        const preparedHorseContacts = await contactService.prepareHorseContacts(
            {
                contactElement: contact,
                horses: contactHorsesIds,
            },
        );

        horseContactsToAdd = preparedHorseContacts.horseContactsToAdd;
        horseContactsToDelete = preparedHorseContacts.horseContactsToDelete;
    }

    return {
        eventContactsToAdd,
        eventContactsToUpdate,
        eventContactsToDelete,
        eventContactRemindersToAdd,
        eventContactRemindersToUpdate,
        eventContactRemindersToDelete,
        horseContactsToAdd,
        horseContactsToDelete,
    };
};

const updateEntriesRelatedData = async ({
    eventId,
    eventStartsTime,
    userId,
    relatedEntriesToUpdate,
    serviceOptions,
}: {
    eventId: string;
    eventStartsTime: string;
    userId: string;
    relatedEntriesToUpdate: EntryModel[];
    serviceOptions: EntryDBServiceOptions;
}) => {
    const entryService = new Entry(serviceOptions);
    const relatedEntries = await entryService.getByParam('event_id', eventId);
    let inventoryChangesToAdd: InventoryChangeModel[] = [];
    const entriesToUpdate = [...relatedEntriesToUpdate];

    if (relatedEntries.length > 0) {
        await Promise.all(
            relatedEntries.map(async (entry) => {
                if (
                    !entriesToUpdate.some(
                        (relatedEntry) => relatedEntry.id === entry.id,
                    )
                ) {
                    const initialLoggedTime = entry.loggedTime;
                    const entryToUpdate = await entry.prepareUpdate(
                        (relatedEntry) => {
                            relatedEntry.loggedTime = eventStartsTime;
                        },
                    );

                    entriesToUpdate.push(entryToUpdate);

                    const entryProducts = await entry.entryProducts.fetch();

                    await Promise.all(
                        entryProducts.map(async (prod) => {
                            const revert =
                                await entryService.prepareRevertInventoryChangeForEntryProduct(
                                    prod,
                                    initialLoggedTime,
                                    eventStartsTime,
                                    userId,
                                );

                            inventoryChangesToAdd.push(...revert);
                        }),
                    );
                }
            }),
        );
    }

    return {
        relatedEntriesToUpdate: entriesToUpdate,
        inventoryChangesToAdd,
    };
};

const updateEventUsersRelatedData = async ({
    membersIds,
    eventId,
    serviceOptions,
}: {
    membersIds: string[] | undefined;
    eventId: string;
    serviceOptions: DBServiceOptionsWithImagesAndValidation;
}) => {
    let eventUsersToAdd: EventUserModel[] = [];
    let eventUsersToDelete: EventUserModel[] = [];

    if (membersIds !== undefined) {
        const eventUserService = new EventUser(serviceOptions);
        const eventUsersBatch = await eventUserService.prepareUpdateBatch(
            membersIds,
            eventId,
        );

        eventUsersToAdd = eventUsersBatch.usersToAdd;
        eventUsersToDelete = eventUsersBatch.usersToDelete;
    }

    return { eventUsersToAdd, eventUsersToDelete };
};

const updateUserRelatedData = async ({
    payload,
    userId,
    database,
}: {
    payload: EventPayload;
    userId: string;
    database: Database;
}) => {
    let userToUpdate: UserModel | null = null;

    if (payload.updateUserTimezone && userId && payload.timezone) {
        const userService = await database.collections
            .get<UserModel>('users')
            .find(userId);
        userToUpdate = await userService.prepareUpdate((userModel) => {
            userModel.timezone = payload.timezone;
        });
    }

    return { userToUpdate };
};

const mapUpdateHorsesRelatedData = (
    data: Awaited<ReturnType<typeof updateHorsesRelatedData>>,
    preparedData: PreparedDataOnAdd[],
) => {
    const newPreparedData: PreparedDataOnAdd[] = [...preparedData];

    newPreparedData.push(
        ...mapRecords(data.horsesToUpdate, 'horses', 'update'),
    );
    newPreparedData.push(
        ...mapRecords(data.eventHorsesToAdd, 'event_horses', 'add'),
    );
    newPreparedData.push(
        ...mapRecords(data.eventHorsesToDelete, 'event_horses', 'delete'),
    );

    return newPreparedData;
};

const mapUpdateContactsRelatedData = (
    data: Awaited<ReturnType<typeof updateContactsRelatedData>>,
    preparedData: PreparedDataOnAdd[],
) => {
    const newPreparedData: PreparedDataOnAdd[] = [...preparedData];

    newPreparedData.push(
        ...mapRecords(data.eventContactsToAdd, 'event_contacts', 'add'),
    );
    newPreparedData.push(
        ...mapRecords(data.eventContactsToUpdate, 'event_contacts', 'update'),
    );
    newPreparedData.push(
        ...mapRecords(data.eventContactsToDelete, 'event_contacts', 'delete'),
    );
    newPreparedData.push(
        ...mapRecords(
            data.eventContactRemindersToAdd,
            'event_contact_reminders',
            'add',
        ),
    );
    newPreparedData.push(
        ...mapRecords(
            data.eventContactRemindersToUpdate,
            'event_contact_reminders',
            'update',
        ),
    );
    newPreparedData.push(
        ...mapRecords(
            data.eventContactRemindersToDelete,
            'event_contact_reminders',
            'delete',
        ),
    );
    newPreparedData.push(
        ...mapRecords(data.horseContactsToAdd, 'horse_contacts', 'add'),
    );
    newPreparedData.push(
        ...mapRecords(data.horseContactsToDelete, 'horse_contacts', 'delete'),
    );

    return newPreparedData;
};

const mapUpdateEntriesRelatedData = (
    data: Awaited<ReturnType<typeof updateEntriesRelatedData>>,
    preparedData: PreparedDataOnAdd[],
) => {
    const newPreparedData: PreparedDataOnAdd[] = [...preparedData];

    newPreparedData.push(
        ...mapRecords(data.relatedEntriesToUpdate, 'entries', 'update'),
    );
    newPreparedData.push(
        ...mapRecords(data.inventoryChangesToAdd, 'inventory_changes', 'add'),
    );

    return newPreparedData;
};

const mapUpdateEventUsersRelatedData = (
    data: Awaited<ReturnType<typeof updateEventUsersRelatedData>>,
    preparedData: PreparedDataOnAdd[],
) => {
    const newPreparedData: PreparedDataOnAdd[] = [...preparedData];

    newPreparedData.push(
        ...mapRecords(data.eventUsersToAdd, 'event_users', 'add'),
    );
    newPreparedData.push(
        ...mapRecords(data.eventUsersToDelete, 'event_users', 'delete'),
    );

    return newPreparedData;
};

const mapUpdateUserRelatedData = (
    data: Awaited<ReturnType<typeof updateUserRelatedData>>,
    preparedData: PreparedDataOnAdd[],
) => {
    const newPreparedData: PreparedDataOnAdd[] = [...preparedData];

    if (data.userToUpdate) {
        newPreparedData.push({
            record: data.userToUpdate,
            table: 'users',
            operation: 'update',
        });
    }

    return newPreparedData;
};

type UpdateRelatedDataBuckets = {
    eventHorsesToAdd: EventHorseModel[];
    eventHorsesToDelete: EventHorseModel[];
    eventContactsToAdd: EventContactModel[];
    eventContactsToUpdate: EventContactModel[];
    eventContactsToDelete: EventContactModel[];
    eventContactRemindersToAdd: EventContactReminderModel[];
    eventContactRemindersToUpdate: EventContactReminderModel[];
    eventContactRemindersToDelete: EventContactReminderModel[];
    relatedEntriesToUpdate: EntryModel[];
    horsesToUpdate: HorseModel[];
    inventoryChangesToAdd: InventoryChangeModel[];
    eventUsersToAdd: EventUserModel[];
    eventUsersToDelete: EventUserModel[];
    horseContactsToAdd: HorseContactModel[];
    horseContactsToDelete: HorseContactModel[];
    userToUpdate: UserModel | null;
};

const logUpdateDBActions = ({
    eventElement,
    buckets,
    logDBAction,
    tableName,
}: {
    eventElement: EventModel;
    buckets: UpdateRelatedDataBuckets;
    logDBAction: DBWriterLoggerFunction;
    tableName: string;
}) => {
    const {
        eventHorsesToAdd,
        eventHorsesToDelete,
        eventContactsToAdd,
        eventContactsToUpdate,
        eventContactsToDelete,
        eventContactRemindersToAdd,
        eventContactRemindersToUpdate,
        eventContactRemindersToDelete,
        relatedEntriesToUpdate,
        horsesToUpdate,
        inventoryChangesToAdd,
        eventUsersToAdd,
        eventUsersToDelete,
        horseContactsToAdd,
        horseContactsToDelete,
        userToUpdate,
    } = buckets;

    if (eventElement) {
        logDBAction({
            message: 'Update event',
            modelName: tableName,
            payload: eventElement,
        });
    }

    if (eventHorsesToAdd.length > 0) {
        logDBAction({
            message: 'Update event - create event horses',
            modelName: tableName,
            payload: eventHorsesToAdd,
        });
    }

    if (eventHorsesToDelete.length > 0) {
        logDBAction({
            message: 'Update event - delete event horses',
            modelName: tableName,
            payload: eventHorsesToDelete,
        });
    }

    if (eventContactsToAdd.length > 0) {
        logDBAction({
            message: 'Update event - create event contacts',
            modelName: tableName,
            payload: eventContactsToAdd,
        });
    }

    if (eventContactsToUpdate.length > 0) {
        logDBAction({
            message: 'Update event - update event contacts',
            modelName: tableName,
            payload: eventContactsToUpdate,
        });
    }

    if (eventContactsToDelete.length > 0) {
        logDBAction({
            message: 'Update event - delete event contacts',
            modelName: tableName,
            payload: eventContactsToDelete,
        });
    }

    if (eventContactRemindersToAdd.length > 0) {
        logDBAction({
            message: 'Update event - create event contact reminders',
            modelName: tableName,
            payload: eventContactRemindersToAdd,
        });
    }

    if (eventContactRemindersToUpdate.length > 0) {
        logDBAction({
            message: 'Update event - update event contact reminders',
            modelName: tableName,
            payload: eventContactRemindersToUpdate,
        });
    }

    if (eventContactRemindersToDelete.length > 0) {
        logDBAction({
            message: 'Update event - delete event contact reminders',
            modelName: tableName,
            payload: eventContactRemindersToDelete,
        });
    }

    if (relatedEntriesToUpdate.length > 0) {
        logDBAction({
            message: 'Update event - update related entries',
            modelName: tableName,
            payload: relatedEntriesToUpdate,
        });
    }

    if (horsesToUpdate.length > 0) {
        logDBAction({
            message: 'Update event - update horses',
            modelName: tableName,
            payload: horsesToUpdate,
        });
    }

    if (inventoryChangesToAdd.length > 0) {
        logDBAction({
            message: 'Update event - create inventory changes',
            modelName: tableName,
            payload: inventoryChangesToAdd,
        });
    }

    const eventUsersToUpdate = [...eventUsersToAdd, ...eventUsersToDelete];

    if (eventUsersToUpdate.length > 0) {
        logDBAction({
            message: 'Update event - update event users',
            modelName: tableName,
            payload: eventUsersToUpdate,
        });
    }

    if (horseContactsToAdd.length > 0) {
        logDBAction({
            message: 'Update event - create horse contacts',
            modelName: tableName,
            payload: horseContactsToAdd,
        });
    }

    if (horseContactsToDelete.length > 0) {
        logDBAction({
            message: 'Update event - delete horse contacts',
            modelName: tableName,
            payload: horseContactsToDelete,
        });
    }

    if (userToUpdate) {
        logDBAction({
            message: 'Update user',
            modelName: tableName,
            payload: userToUpdate,
        });
    }
};

export {
    prepareUpdateLocation,
    prepareUpdateEvent,
    updateHorsesRelatedData,
    updateContactsRelatedData,
    updateEntriesRelatedData,
    updateEventUsersRelatedData,
    updateUserRelatedData,
    mapUpdateHorsesRelatedData,
    mapUpdateContactsRelatedData,
    mapUpdateEntriesRelatedData,
    mapUpdateEventUsersRelatedData,
    mapUpdateUserRelatedData,
    logUpdateDBActions,
};

export type { UpdateRelatedDataBuckets };
